import { useMemo } from 'react';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { CodeEditor } from 'src/pages/ClinicalTrial/Readings/ReadingInventoryItemForm/CodeEditor';
import { useInventoryFormHandler } from '@hooks/useInventoryFormHandler';
import { runToast } from 'src/store-redux/tools';

const TEMPLATE_METADATA_COMMENT = /<!--\s*([^>]*?)\s*-->/;
const COMMIT_BASE_URL =
  'https://bitbucket.org/image-analysis-group/d7-templates/commits';

const parseTemplateMetadata = (html: string) => {
  const comment = html.match(TEMPLATE_METADATA_COMMENT)?.[1] ?? '';
  const read = (key: 'CODE' | 'DATE' | 'COMMIT') => {
    const match = comment.match(
      new RegExp(`(?:^|;)\\s*${key}\\s*:\\s*([^;]+?)\\s*(?=;|$)`)
    );
    return match ? match[1].trim() : '';
  };
  return {
    code: read('CODE'),
    date: read('DATE'),
    commit: read('COMMIT'),
  };
};

export const ReportTemplate = () => {
  const { onChange, watch } = useInventoryFormHandler();

  const templateInfo = useMemo(() => {
    const template = watch('template');
    if (typeof template !== 'string' || !template) return null;
    const { code, date, commit } = parseTemplateMetadata(template);
    if (!code && !date && !commit) return null;
    return { code, date, commit };
  }, [watch('template')]);

  const fetchLatestTemplate = (reportTemplateCode: string) => async () => {
    try {
      const response = await fetch(
        `/reports/html/${reportTemplateCode}/${reportTemplateCode}.html`
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }
      const html = await response.text();
      onChange('template', html);

      const { code, date, commit } = parseTemplateMetadata(html);
      const summary = [date, code, commit && `commit: ${commit}`]
        .filter(Boolean)
        .join(', ');
      runToast('info', summary || 'Template loaded');
    } catch (error) {
      console.error('Error fetching the latest template:', error);

      runToast(
        'error',
        (error as Error).message,
        'Failed to fetch the latest template'
      );
    }
  };

  return (
    <>
      <div className='w-full col-span-2'>
        <Button
          onClick={fetchLatestTemplate(watch('code'))}
          type='primary'
          size='small'
        >
          Fetch Latest Template
        </Button>
        <div className='w-full text-arsenic font-semibold mt-1'>
          Current:{' '}
          {templateInfo && (
            <>
              {templateInfo.date} | {templateInfo.code} |{' '}
              {templateInfo.commit && (
                <a
                  href={`${COMMIT_BASE_URL}/${templateInfo.commit}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='underline'
                >
                  {templateInfo.commit}
                </a>
              )}
            </>
          )}
        </div>
      </div>
      <CodeEditor
        classNameWrapper={'col-span-2'}
        title={'HTML template'}
        language={'html'}
        value={watch('template')}
        onChange={value => onChange('template', value)}
      />
    </>
  );
};
